// src/hooks/index.ts
// All data hooks — talk directly to Supabase, no Express needed

import { useState, useEffect, useCallback, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase, db, realtime } from '../lib/supabase';

// ── Generic query hook ────────────────────────────────────────────────────────
export function useQuery<T>(
  fetcher: () => PromiseLike<{ data: T | null; error: any }>,
  deps: any[] = []
) {
  const [data, setData]       = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await fetcher();
    if (error) setError(error.message);
    else setData(data);
    setLoading(false);
  // eslint-disable-next-line
  }, deps);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refresh: load };
}

// ── Players ───────────────────────────────────────────────────────────────────
export function usePlayer(playerId?: string) {
  return useQuery(
    () => db.players()
      .select(`*, player_career_stats(*), club_members(club_role, is_active, clubs(id, name, logo_url, primary_color))`)
      .eq('id', playerId!)
      .single(),
    [playerId]
  );
}

export function useMyProfile(userId?: string) {
  return useQuery(
    () => db.players()
      .select(`*, player_career_stats(*)`)
      .eq('user_id', userId!)
      .single(),
    [userId]
  );
}

export function useFreeAgents(filters: { role?: string; city?: string; bowling?: string } = {}) {
  return useQuery(
    () => {
      let q = db.players()
        .select(`id, display_name, city, player_role, batting_style, bowling_style, is_free_agent, player_career_stats(bat_runs, bat_matches, bowl_wickets, man_of_match)`)
        .eq('is_free_agent', true)
        .neq('profile_visibility', 'private');
      if (filters.role)    q = q.eq('player_role', filters.role);
      if (filters.city)    q = q.ilike('city', `%${filters.city}%`);
      if (filters.bowling) q = q.ilike('bowling_style', `%${filters.bowling}%`);
      return q.order('display_name');
    },
    [filters.role, filters.city, filters.bowling]
  );
}

// ── Clubs ─────────────────────────────────────────────────────────────────────
export function useClub(clubId?: string) {
  return useQuery(
    () => db.clubs()
      .select(`*, club_stats(*), club_members(id, club_role, admin_roles, membership_status, jersey_number, is_active, players(id, display_name, player_role, city, profile_visibility))`)
      .eq('id', clubId!)
      .eq('club_members.is_active', true)
      .single(),
    [clubId]
  );
}

export function useClubs(city?: string) {
  return useQuery(
    () => {
      let q = db.clubs().select(`*, club_stats(*)`).eq('is_active', true);
      if (city) q = q.ilike('city', `%${city}%`);
      return q.order('name');
    },
    [city]
  );
}

// ── Matches ───────────────────────────────────────────────────────────────────
export function useLiveMatches() {
  return useQuery(
    () => db.matches()
      .select(`*, club1:clubs!matches_club1_id_fkey(name, logo_url, primary_color), club2:clubs!matches_club2_id_fkey(name, logo_url, primary_color), tournaments(name), venues(name, city)`)
      .in('status', ['live', 'scheduled'])
      .order('scheduled_at', { ascending: true })
      .limit(10),
    []
  );
}

export function useMatch(matchId?: string) {
  const [match, setMatch]   = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!matchId) return;
    const { data } = await db.matches()
      .select(`*, club1:clubs!matches_club1_id_fkey(id, name, logo_url, primary_color), club2:clubs!matches_club2_id_fkey(id, name, logo_url, primary_color), tournaments(name), venues(name, city), mom:players!matches_man_of_match_id_fkey(display_name), innings(*, batting_scorecards(*, striker:players!batting_scorecards_player_id_fkey(display_name), dismissedBy:players!batting_scorecards_bowler_id_fkey(display_name)), bowling_scorecards(*, players(display_name)))`)
      .eq('id', matchId)
      .single();
    setMatch(data);
    setLoading(false);
  }, [matchId]);

  useEffect(() => { load(); }, [load]);
  return { match, loading, refresh: load };
}

export function useRecentMatches(limit = 5) {
  return useQuery(
    () => db.matches()
      .select(`id, status, format, innings1_runs, innings1_wickets, innings1_overs, innings2_runs, innings2_wickets, result_text, scheduled_at, club1:clubs!matches_club1_id_fkey(name), club2:clubs!matches_club2_id_fkey(name), mom:players!matches_man_of_match_id_fkey(display_name)`)
      .eq('status', 'completed')
      .order('completed_at', { ascending: false })
      .limit(limit),
    [limit]
  );
}

// ── Live scoring with Supabase Realtime ───────────────────────────────────────
export function useLiveScoring(inningsId?: string) {
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [innings, setInnings]       = useState<any>(null);
  const channelRef                  = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!inningsId) return;

    // Load initial innings state
    db.innings().select('*').eq('id', inningsId).single()
      .then(({ data }) => setInnings(data));

    // Load recent deliveries
    db.deliveries()
      .select(`*, players!deliveries_striker_id_fkey(display_name), players!deliveries_bowler_id_fkey(display_name)`)
      .eq('innings_id', inningsId)
      .order('over_number', { ascending: false })
      .order('ball_number', { ascending: false })
      .limit(36)
      .then(({ data }) => setDeliveries(data || []));

    // Subscribe to new deliveries in real time
    channelRef.current = supabase
      .channel(`scoring-${inningsId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'deliveries',
        filter: `innings_id=eq.${inningsId}`,
      }, (payload) => {
        setDeliveries(prev => [payload.new, ...prev.slice(0, 35)]);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'innings',
        filter: `id=eq.${inningsId}`,
      }, (payload) => {
        setInnings(payload.new);
      })
      .subscribe();

    return () => {
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch { /* ignore */ }
        channelRef.current = null;
      }
    };
  }, [inningsId]);

  // Score a delivery — writes directly to Supabase
  const scoreDelivery = useCallback(async (delivery: {
    inningsId: string; overNumber: number; ballNumber: number;
    bowlerId: string; strikerId: string; nonStrikerId?: string;
    runsBat: number; extraRuns: number; extraType?: string;
    isLegalBall: boolean; isWicket: boolean; wicketType?: string;
    outPlayerId?: string; fielderId?: string; isFreeHit?: boolean;
  }) => {
    const totalRuns = delivery.runsBat + delivery.extraRuns;

    // Insert delivery
    const { error } = await db.deliveries().insert({
      innings_id:      delivery.inningsId,
      over_number:     delivery.overNumber,
      ball_number:     delivery.ballNumber,
      bowler_id:       delivery.bowlerId,
      striker_id:      delivery.strikerId,
      non_striker_id:  delivery.nonStrikerId,
      runs_off_bat:    delivery.runsBat,
      extra_runs:      delivery.extraRuns,
      extra_type:      delivery.extraType || null,
      total_runs:      totalRuns,
      is_legal_ball:   delivery.isLegalBall,
      is_wicket:       delivery.isWicket,
      wicket_type:     delivery.wicketType || null,
      out_player_id:   delivery.outPlayerId || null,
      fielder_id:      delivery.fielderId || null,
      is_free_hit:     delivery.isFreeHit || false,
    });

    if (error) throw new Error(error.message);

    // Update innings totals via RPC (Supabase stored procedure). Previously
    // the error from this call was discarded — the delivery row would
    // write but the innings totals (runs/wickets/overs) silently
    // desynced. Now propagated so the scorer sees the failure.
    const { error: rpcErr } = await supabase.rpc('update_innings_totals', {
      p_innings_id:   delivery.inningsId,
      p_runs:         totalRuns,
      p_wicket:       delivery.isWicket ? 1 : 0,
      p_legal_ball:   delivery.isLegalBall ? 1 : 0,
      p_wide:         delivery.extraType === 'wide' ? delivery.extraRuns : 0,
      p_noball:       delivery.extraType === 'no_ball' ? delivery.extraRuns : 0,
      p_bye:          delivery.extraType === 'bye' ? delivery.extraRuns : 0,
      p_legbye:       delivery.extraType === 'leg_bye' ? delivery.extraRuns : 0,
    });
    if (rpcErr) throw new Error(`Innings totals failed: ${rpcErr.message}`);

  }, []);

  return { deliveries, innings, scoreDelivery };
}

// ── Tournaments ───────────────────────────────────────────────────────────────
export function useTournaments(status?: string) {
  return useQuery(
    () => {
      let q = db.tournaments()
        .select(`*, authorities(name, short_name), tournament_registrations(count)`)
        .order('start_date', { ascending: true });
      if (status) q = q.eq('status', status);
      return q;
    },
    [status]
  );
}

export function useTournament(id?: string) {
  return useQuery(
    () => db.tournaments()
      .select(`*, authorities(name, logo_url), tournament_registrations(id, status, applied_at, clubs(id, name, logo_url, primary_color, city)), tournament_groups(id, name, tournament_group_clubs(*, clubs(name, logo_url, primary_color)))`)
      .eq('id', id!)
      .single(),
    [id]
  );
}

// ── Notifications ─────────────────────────────────────────────────────────────
export function useNotifications(userId?: string) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!userId) return;

    db.notifications()
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => setNotifications(data || []));

    // Remove any stale channel with the same topic (StrictMode double-mount safety)
    const topic = `notif-${userId}`;
    supabase.getChannels()
      .filter(c => c.topic === `realtime:${topic}` || c.topic === topic)
      .forEach(c => { try { supabase.removeChannel(c); } catch {} });

    const ch = supabase
      .channel(topic)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => setNotifications(prev => [payload.new, ...prev])
      )
      .subscribe();
    channelRef.current = ch;

    return () => {
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch {}
        channelRef.current = null;
      }
    };
  }, [userId]);

  const unread = notifications.filter(n => !n.is_read).length;

  const markRead = useCallback(async (id: string) => {
    await db.notifications().update({ is_read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  }, []);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    await db.notifications().update({ is_read: true }).eq('user_id', userId);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  }, [userId]);

  return { notifications, unread, markRead, markAllRead };
}

// ── Auction Realtime ──────────────────────────────────────────────────────────
export function useAuctionRoom(auctionId?: string) {
  const [room, setRoom]     = useState<any>(null);
  const [bids, setBids]     = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef            = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!auctionId) return;

    db.auctionRooms()
      .select(`*, tournaments(name, format), auction_players(*, players(display_name, player_role, city, batting_style, bowling_style, player_career_stats(bat_runs, bowl_wickets, man_of_match))), auction_teams(*, clubs(name, logo_url, primary_color))`)
      .eq('id', auctionId)
      .single()
      .then(({ data }) => { setRoom(data); setLoading(false); });

    db.auctionBids()
      .select(`*, clubs(name, primary_color)`)
      .eq('auction_room_id', auctionId)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => setBids(data || []));

    channelRef.current = supabase
      .channel(`auction-${auctionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'auction_bids', filter: `auction_room_id=eq.${auctionId}` },
        (payload) => setBids(prev => [payload.new, ...prev.slice(0, 29)])
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'auction_rooms', filter: `id=eq.${auctionId}` },
        (payload) => setRoom((r: any) => ({ ...r, ...payload.new }))
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch { /* ignore */ }
        channelRef.current = null;
      }
    };
  }, [auctionId]);

  const placeBid = useCallback(async (playerId: string, clubId: string, amount: number) => {
    const { error } = await db.auctionBids().insert({
      auction_room_id: auctionId, player_id: playerId,
      bidding_club_id: clubId, amount,
    });
    if (error) throw new Error(error.message);
  }, [auctionId]);

  return { room, bids, loading, placeBid };
}

// ── Feed ──────────────────────────────────────────────────────────────────────
export function useFeed(clubId?: string) {
  return useQuery(
    () => {
      let q = db.feed()
        .select(`*, users:author_id(raw_user_meta_data), clubs(name, primary_color)`)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(30);
      if (clubId) q = q.eq('club_id', clubId);
      return q;
    },
    [clubId]
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// API compatibility layer — maps old Express REST calls to Supabase queries
// ══════════════════════════════════════════════════════════════════════════════

function parseQS(url: string): Record<string, string> {
  const idx = url.indexOf('?');
  if (idx < 0) return {};
  const obj: Record<string, string> = {};
  new URLSearchParams(url.slice(idx + 1)).forEach((v, k) => { obj[k] = v; });
  return obj;
}
function pathOf(url: string) { return url.split('?')[0]; }

export const api = {
  async get(url: string): Promise<any> {
    const path = pathOf(url);
    const qs = parseQS(url);

    // ── Public matches ───────────────────────────
    if (path === '/public/matches' || (path.startsWith('/public/matches/') && !path.includes('/live'))) {
      const id = path.split('/public/matches/')[1];
      if (id) {
        const { data } = await db.matches()
          .select(`*, club1:clubs!matches_club1_id_fkey(id,name,logo_url,primary_color), club2:clubs!matches_club2_id_fkey(id,name,logo_url,primary_color), tournaments(name), venues(name,city), mom:players!matches_man_of_match_id_fkey(display_name), innings(*, batting_scorecards(*, striker:players!batting_scorecards_player_id_fkey(display_name)), bowling_scorecards(*, players(display_name)))`)
          .eq('id', id).single();
        return data ? _mapMatch(data) : null;
      }
      let q = db.matches()
        .select(`*, club1:clubs!matches_club1_id_fkey(name,logo_url,primary_color), club2:clubs!matches_club2_id_fkey(name,logo_url,primary_color), tournaments(name), venues(name,city), mom:players!matches_man_of_match_id_fkey(display_name)`)
        .order('scheduled_at', { ascending: false })
        .limit(Number(qs.limit) || 20);
      if (qs.status) q = q.in('status', qs.status.split(','));
      const { data } = await q;
      return (data || []).map(_mapMatch);
    }

    // ── Matches list (authed) ────────────────────
    if (path === '/matches') {
      let q = db.matches()
        .select(`*, club1:clubs!matches_club1_id_fkey(name,logo_url,primary_color), club2:clubs!matches_club2_id_fkey(name,logo_url,primary_color), tournaments(name), venues(name,city)`)
        .order('scheduled_at', { ascending: true })
        .limit(Number(qs.limit) || 20);
      if (qs.status) q = q.in('status', qs.status.split(','));
      const { data } = await q;
      return (data || []).map(_mapMatch);
    }

    // ── Match detail ─────────────────────────────
    const matchById = path.match(/^\/matches\/([^/]+)$/);
    if (matchById) {
      const { data } = await db.matches()
        .select(`*, club1:clubs!matches_club1_id_fkey(id,name,logo_url,primary_color), club2:clubs!matches_club2_id_fkey(id,name,logo_url,primary_color), tournaments(name), venues(name,city), mom:players!matches_man_of_match_id_fkey(display_name), innings(*, batting_scorecards(*, striker:players!batting_scorecards_player_id_fkey(display_name), dismissedBy:players!batting_scorecards_bowler_id_fkey(display_name)), bowling_scorecards(*, players(display_name)))`)
        .eq('id', matchById[1]).single();
      return data ? _mapMatch(data) : null;
    }

    // ── Match live innings ──────────────────────
    const matchLive = path.match(/^\/matches\/([^/]+)\/live$/);
    if (matchLive) {
      const { data } = await db.innings()
        .select(`*, batting_scorecards(*, striker:players!batting_scorecards_player_id_fkey(display_name)), bowling_scorecards(*, players(display_name)), deliveries(*)`)
        .eq('match_id', matchLive[1])
        .order('innings_number');
      return data || [];
    }

    // ── Marketplace (free agents) ────────────────
    // Cap at 100 rows. Avoid rendering an unbounded DOM list. Pagination
    // can be added later via a `page` query param.
    if (path === '/marketplace') {
      let q = db.players()
        .select(`id, display_name, city, player_role, batting_style, bowling_style, is_free_agent, media_urls, social_links, player_career_stats(bat_runs, bat_matches, bat_innings, bat_balls, bat_not_outs, bat_high_score, bat_fifties, bat_hundreds, bowl_wickets, bowl_balls, bowl_runs, bowl_maidens, catches, man_of_match)`)
        .eq('is_free_agent', true)
        .neq('profile_visibility', 'private');
      if (qs.role) q = q.eq('player_role', qs.role);
      if (qs.search) q = q.ilike('display_name', `%${qs.search}%`);
      const { data } = await q.order('display_name').limit(100);
      return (data || []).map(_mapPlayer);
    }

    // ── Player detail ────────────────────────────
    const playerById = path.match(/^\/players\/([^/]+)$/);
    if (playerById && playerById[1] !== 'me') {
      const { data } = await db.players()
        .select(`*, player_career_stats(*), club_members(club_role, is_active, clubs(id, name, logo_url, primary_color))`)
        .eq('id', playerById[1]).single();
      return data ? _mapPlayer(data) : null;
    }

    // ── Players me ───────────────────────────────
    if (path === '/players/me') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await db.players()
        .select(`*, player_career_stats(*), club_members(club_role, is_active, clubs(id, name, logo_url, primary_color))`)
        .eq('user_id', user.id).single();
      return data ? _mapPlayer(data) : null;
    }

    // ── Player AI analysis ───────────────────────
    const playerAi = path.match(/^\/players\/([^/]+)\/ai-analysis$/);
    if (playerAi) {
      const playerId = playerAi[1] === 'me'
        ? (await supabase.auth.getUser()).data.user?.id
        : playerAi[1];
      try {
        const { data } = await supabase.functions.invoke('ai-analysis', { body: { playerId } });
        return data;
      } catch { return null; }
    }

    // ── Club detail ──────────────────────────────
    const clubById = path.match(/^\/clubs\/([^/]+)$/);
    if (clubById) {
      const clubId = clubById[1];
      if (clubId === 'me') {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        // Look up player first, then find their club membership
        const { data: player } = await db.players().select('id').eq('user_id', user.id).maybeSingle();
        if (!player) return null;
        const { data: member } = await db.clubMembers()
          .select('club_id').eq('player_id', player.id).eq('is_active', true).maybeSingle();
        if (!member) return null;
        const { data } = await db.clubs()
          .select(`*, club_stats(*), club_members(id, club_role, admin_roles, membership_status, jersey_number, is_active, players(id, display_name, player_role, city, profile_visibility))`)
          .eq('id', member.club_id).single();
        return _mapClub(data);
      }
      const { data } = await db.clubs()
        .select(`*, club_stats(*), club_members(id, club_role, admin_roles, membership_status, jersey_number, is_active, players(id, display_name, player_role, city, profile_visibility))`)
        .eq('id', clubId).single();
      return _mapClub(data);
    }

    // ── Tournaments list ─────────────────────────
    if (path === '/tournaments') {
      const { data } = await db.tournaments()
        .select(`*, authorities(name, short_name), tournament_registrations(count)`)
        .order('start_date', { ascending: true });
      return (data || []).map(_mapTournament);
    }

    // ── Tournament detail ────────────────────────
    const tournById = path.match(/^\/tournaments\/([^/]+)$/);
    if (tournById) {
      const { data } = await db.tournaments()
        .select(`*, authorities(name, logo_url), tournament_registrations(id, status, applied_at, clubs(id, name, logo_url, primary_color, city)), tournament_groups(id, name, tournament_group_clubs(*, clubs(name, logo_url, primary_color)))`)
        .eq('id', tournById[1]).single();
      return _mapTournament(data);
    }

    // ── Tournament matches ───────────────────────
    const tournMatches = path.match(/^\/tournaments\/([^/]+)\/matches$/);
    if (tournMatches) {
      const { data } = await db.matches()
        .select(`*, club1:clubs!matches_club1_id_fkey(name,logo_url,primary_color), club2:clubs!matches_club2_id_fkey(name,logo_url,primary_color), venues(name)`)
        .eq('tournament_id', tournMatches[1])
        .order('scheduled_at');
      return (data || []).map(_mapMatch);
    }

    // ── Umpires ──────────────────────────────────
    if (path === '/umpires') {
      const { data: u } = await db.umpires().select('*').order('created_at', { ascending: false });
      if (!u || u.length === 0) return [];
      const userIds = u.map((x: any) => x.user_id).filter(Boolean);
      const { data: ps } = userIds.length
        ? await db.players().select('user_id, display_name').in('user_id', userIds)
        : { data: [] as any[] };
      const nameMap = new Map((ps || []).map((p: any) => [p.user_id, p.display_name]));
      return u.map((x: any) => ({ ...x, full_name: nameMap.get(x.user_id) || 'Umpire' }));
    }

    // ── Venues ───────────────────────────────────
    if (path === '/venues') {
      const { data } = await db.venues().select('*').order('name');
      return data || [];
    }
    const venueById = path.match(/^\/venues\/([^/]+)$/);
    if (venueById) {
      const { data } = await db.venues()
        .select(`*, venue_slots(*)`)
        .eq('id', venueById[1]).single();
      return data;
    }

    // ── Transfers (mine — filtered for current player) ──
    if (path === '/transfers/mine') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: player } = await db.players().select('id').eq('user_id', user.id).maybeSingle();
      if (!player) return [];
      const { data } = await db.transfers()
        .select(`*, players(display_name, player_role), from_club:clubs!player_transfers_from_club_id_fkey(name), to_club:clubs!player_transfers_to_club_id_fkey(name)`)
        .or(`from_player_id.eq.${player.id},to_player_id.eq.${player.id}`)
        .order('created_at', { ascending: false });
      return data || [];
    }

    // ── Transfers (all — for admins/managers) ────
    if (path === '/transfers') {
      const { data } = await db.transfers()
        .select(`*, players(display_name, player_role), from_club:clubs!player_transfers_from_club_id_fkey(name), to_club:clubs!player_transfers_to_club_id_fkey(name)`)
        .order('created_at', { ascending: false });
      return data || [];
    }

    // ── Disputes ─────────────────────────────────
    if (path === '/disputes') {
      const { data } = await db.disputes()
        .select(`*, clubs!disputes_raised_by_club_fkey(name), matches(id)`)
        .order('created_at', { ascending: false });
      return data || [];
    }

    // ── Auction list ─────────────────────────────
    if (path === '/auction') {
      const { data } = await db.auctionRooms()
        .select(`*, tournaments(name, format)`)
        .order('created_at', { ascending: false });
      return data || [];
    }
    const auctionById = path.match(/^\/auction\/([^/]+)$/);
    if (auctionById) {
      const { data } = await db.auctionRooms()
        .select(`*, tournaments(name, format), auction_players(*, players(display_name, player_role, city, batting_style, bowling_style, player_career_stats(bat_runs, bowl_wickets, man_of_match))), auction_teams(*, clubs(name, logo_url, primary_color))`)
        .eq('id', auctionById[1]).single();
      return data;
    }

    // ── Feed ─────────────────────────────────────
    if (path === '/feed') {
      const { data } = await db.feed()
        .select(`*, clubs(name, primary_color)`)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(30);
      const rows = data || [];
      if (rows.length === 0) return rows;
      const authorIds = Array.from(new Set(rows.map((r: any) => r.author_id).filter(Boolean)));
      const { data: ps } = authorIds.length
        ? await db.players().select('user_id, display_name').in('user_id', authorIds)
        : { data: [] as any[] };
      const nameMap = new Map((ps || []).map((p: any) => [p.user_id, p.display_name]));
      return rows.map((r: any) => ({
        ...r,
        author_name: nameMap.get(r.author_id) || 'Member',
        club_name: r.clubs?.name,
        primary_color: r.clubs?.primary_color,
      }));
    }

    // ── Junior profiles ──────────────────────────
    if (path === '/junior/club/me') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: player } = await db.players().select('id').eq('user_id', user.id).maybeSingle();
      if (!player) return [];
      const { data: member } = await db.clubMembers()
        .select('club_id').eq('player_id', player.id).eq('is_active', true).maybeSingle();
      if (!member) return [];
      const { data } = await db.juniors()
        .select('*, players!inner(id, display_name, club_members!inner(club_id, is_active))')
        .eq('players.club_members.club_id', member.club_id)
        .eq('players.club_members.is_active', true)
        .order('created_at', { ascending: false });
      return data || [];
    }

    // ── Payments ─────────────────────────────────
    if (path === '/payments') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data } = await db.payments()
        .select('*').eq('payer_user_id', user.id).order('created_at', { ascending: false });
      return data || [];
    }

    // ── Player search (all players) ─────────────
    if (path === '/players/search') {
      const q = qs.q;
      if (!q) return [];
      const { data } = await db.players()
        .select(`id, display_name, player_role, city, is_free_agent, club_members(club_role, is_active, clubs(name))`)
        .ilike('display_name', `%${q}%`)
        .limit(10);
      return (data || []).map((p: any) => {
        const activeMember = Array.isArray(p.club_members) ? p.club_members.find((m: any) => m.is_active) : null;
        return {
          id: p.id,
          displayName: p.display_name,
          playerRole: p.player_role,
          city: p.city,
          isFreeAgent: p.is_free_agent,
          clubName: activeMember?.clubs?.name || null,
        };
      });
    }

    // ── Dashboard stats ────────────────────────
    if (path === '/dashboard-stats') {
      const [playersRes, freeAgentsRes, clubsRes] = await Promise.all([
        db.players().select('id', { count: 'exact', head: true }),
        db.players().select('id', { count: 'exact', head: true }).eq('is_free_agent', true),
        db.clubs().select('city', { count: 'exact' }).eq('is_active', true),
      ]);
      const cities = new Set((clubsRes.data || []).map((c: any) => c.city).filter(Boolean));
      return {
        players: playersRes.count || 0,
        freeAgents: freeAgentsRes.count || 0,
        clubs: clubsRes.count || 0,
        cities: cities.size,
      };
    }

    // ── Key contacts for club/tournament ────────
    const keyContacts = path.match(/^\/(clubs|tournaments)\/([^/]+)\/contacts$/);
    if (keyContacts) {
      const entityType = keyContacts[1] === 'clubs' ? 'club' : 'tournament';
      const { data } = await db.keyContacts()
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', keyContacts[2])
        .order('created_at');
      return (data || []).map((c: any) => ({
        id: c.id, contactName: c.contact_name, role: c.role,
        phone: c.phone, isWhatsapp: c.is_whatsapp,
      }));
    }

    // ── Photo gallery ─────────────────────────────
    const gallery = path.match(/^\/(clubs|tournaments|players)\/([^/]+)\/gallery$/);
    if (gallery) {
      const typeMap: Record<string,string> = { clubs: 'club', tournaments: 'tournament', players: 'player' };
      const { data } = await db.photoGallery()
        .select('*')
        .eq('entity_type', typeMap[gallery[1]])
        .eq('entity_id', gallery[2])
        .order('created_at', { ascending: false });
      return (data || []).map((p: any) => ({
        id: p.id, imageUrl: p.image_url, caption: p.caption, createdAt: p.created_at,
      }));
    }

    // ── Practice sessions for a club ────────────
    const practiceSessions = path.match(/^\/clubs\/([^/]+)\/practice-sessions$/);
    if (practiceSessions) {
      const { data } = await db.practiceSessions()
        .select(`*, practice_attendance(id, player_id, status, responded_at, players(display_name))`)
        .eq('club_id', practiceSessions[1])
        .eq('cancelled', false)
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true });
      return (data || []).map((s: any) => ({
        id: s.id,
        clubId: s.club_id,
        title: s.title,
        scheduledAt: s.scheduled_at,
        location: s.location,
        message: s.message,
        createdBy: s.created_by,
        createdAt: s.created_at,
        cancelled: s.cancelled,
        attendance: (s.practice_attendance || []).map((a: any) => ({
          id: a.id,
          playerId: a.player_id,
          playerName: a.players?.display_name,
          status: a.status,
          respondedAt: a.responded_at,
        })),
      }));
    }

    console.warn('[api.get] Unhandled route:', url);
    return null;
  },

  async post(url: string, body?: any): Promise<any> {
    const path = pathOf(url);

    // ── Umpire registration ──────────────────────
    if (path === '/umpires/register') {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await db.umpires().insert({ ...body, user_id: user?.id }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Venue creation ───────────────────────────
    if (path === '/venues') {
      const { data, error } = await db.venues().insert(body).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Disputes ─────────────────────────────────
    if (path === '/disputes') {
      const { data, error } = await db.disputes().insert(body).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Auction bid ──────────────────────────────
    const auctionBid = path.match(/^\/auction\/([^/]+)\/bid$/);
    if (auctionBid) {
      const { error } = await db.auctionBids().insert({
        auction_room_id: auctionBid[1],
        player_id: body.playerId,
        bidding_club_id: body.clubId,
        amount: body.amount,
      });
      if (error) throw new Error(error.message);
      return { ok: true };
    }

    // ── DLS calculator ───────────────────────────
    if (path === '/dls/calculate') {
      const { data, error } = await supabase.functions.invoke('dls', { body });
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Feed post ────────────────────────────────
    if (path === '/feed') {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await db.feed().insert({ ...body, author_id: user?.id, is_public: true }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Feed like ────────────────────────────────
    const feedLike = path.match(/^\/feed\/([^/]+)\/like$/);
    if (feedLike) {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await db.feedLikes().insert({ post_id: feedLike[1], user_id: user?.id });
      if (error && !error.message.includes('duplicate')) throw new Error(error.message);
      return { ok: true };
    }

    // ── Junior register ──────────────────────────
    if (path === '/junior/register') {
      const { data, error } = await db.juniors().insert(body).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Add key contact ───────────────────────────
    const addContact = path.match(/^\/(clubs|tournaments)\/([^/]+)\/contacts$/);
    if (addContact) {
      const entityType = addContact[1] === 'clubs' ? 'club' : 'tournament';
      const { data, error } = await db.keyContacts().insert({
        entity_type: entityType, entity_id: addContact[2],
        contact_name: body.contactName, role: body.role,
        phone: body.phone, is_whatsapp: body.isWhatsapp ?? true,
      }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Add photo to gallery ────────────────────
    const addPhoto = path.match(/^\/(clubs|tournaments|players)\/([^/]+)\/gallery$/);
    if (addPhoto) {
      const { data: { user } } = await supabase.auth.getUser();
      const typeMap: Record<string,string> = { clubs: 'club', tournaments: 'tournament', players: 'player' };
      const { data, error } = await db.photoGallery().insert({
        entity_type: typeMap[addPhoto[1]], entity_id: addPhoto[2],
        image_url: body.imageUrl, caption: body.caption,
        uploaded_by: user?.id,
      }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Create practice session ─────────────────
    const createPractice = path.match(/^\/clubs\/([^/]+)\/practice-sessions$/);
    if (createPractice) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data: player } = await db.players().select('id').eq('user_id', user.id).maybeSingle();
      if (!player) throw new Error('No player profile');
      const { data, error } = await db.practiceSessions().insert({
        club_id: createPractice[1],
        title: body.title || 'Practice Session',
        scheduled_at: body.scheduledAt,
        location: body.location || null,
        message: body.message || null,
        created_by: player.id,
      }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Respond to practice session ─────────────
    const respondPractice = path.match(/^\/practice-sessions\/([^/]+)\/attend$/);
    if (respondPractice) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data: player } = await db.players().select('id').eq('user_id', user.id).maybeSingle();
      if (!player) throw new Error('No player profile');
      const { data, error } = await db.practiceAttendance().upsert({
        session_id: respondPractice[1],
        player_id: player.id,
        status: body.status,
        responded_at: new Date().toISOString(),
      }, { onConflict: 'session_id,player_id' }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    console.warn('[api.post] Unhandled route:', url);
    return null;
  },

  async put(url: string, body?: any): Promise<any> {
    const path = pathOf(url);

    // ── Update own player profile ────────────────
    if (path === '/players/me') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      // Allowlist the fields a client is permitted to set on its own
      // players row. Without this, the camelCase→snake_case mapper would
      // happily forward any column (user_id, profile_visibility, …) the
      // caller chose to include in the payload.
      const PLAYERS_SELF_UPDATE_ALLOWED = new Set([
        'display_name',
        'date_of_birth',
        'city',
        'country',
        'bio',
        'player_role',
        'batting_style',
        'bowling_style',
        'preferred_formats',
        'jersey_number',
        'is_free_agent',
        'media_urls',
        'lat',
        'lng',
      ]);
      const mapped: Record<string, any> = {};
      for (const [k, v] of Object.entries(body || {})) {
        const snake = k.replace(/[A-Z]/g, c => '_' + c.toLowerCase());
        if (PLAYERS_SELF_UPDATE_ALLOWED.has(snake)) mapped[snake] = v;
      }
      if (Object.keys(mapped).length === 0) {
        throw new Error('No allowed fields supplied for /players/me update');
      }
      const { data, error } = await db.players().update(mapped).eq('user_id', user.id).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Update club ──────────────────────────────
    const clubUpdate = path.match(/^\/clubs\/([^/]+)$/);
    if (clubUpdate) {
      const mapped: Record<string, any> = {};
      for (const [k, v] of Object.entries(body || {})) {
        mapped[k.replace(/[A-Z]/g, c => '_' + c.toLowerCase())] = v;
      }
      const { data, error } = await db.clubs().update(mapped).eq('id', clubUpdate[1]).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    console.warn('[api.put] Unhandled route:', url);
    return null;
  },

  async patch(url: string, body?: any): Promise<any> {
    const path = pathOf(url);

    // ── Transfers ────────────────────────────────
    const transferPatch = path.match(/^\/transfers\/([^/]+)$/);
    if (transferPatch) {
      const { data, error } = await db.transfers().update(body).eq('id', transferPatch[1]).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Club member role ──────────────────────────
    const memberPatch = path.match(/^\/clubs\/([^/]+)\/members\/([^/]+)$/);
    if (memberPatch) {
      const { data, error } = await db.clubMembers()
        .update({ club_role: body.clubRole })
        .eq('club_id', memberPatch[1])
        .eq('player_id', memberPatch[2])
        .select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Disputes ─────────────────────────────────
    const disputePatch = path.match(/^\/disputes\/([^/]+)$/);
    if (disputePatch) {
      const mapped: Record<string, any> = {};
      for (const [k, v] of Object.entries(body || {})) {
        mapped[k.replace(/[A-Z]/g, c => '_' + c.toLowerCase())] = v;
      }
      const { data, error } = await db.disputes().update(mapped).eq('id', disputePatch[1]).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    // ── Matches ──────────────────────────────────
    const matchPatch = path.match(/^\/matches\/([^/]+)$/);
    if (matchPatch) {
      const mapped: Record<string, any> = {};
      for (const [k, v] of Object.entries(body || {})) {
        mapped[k.replace(/[A-Z]/g, c => '_' + c.toLowerCase())] = v;
      }
      const { data, error } = await db.matches().update(mapped).eq('id', matchPatch[1]).select().single();
      if (error) throw new Error(error.message);
      return data;
    }

    console.warn('[api.patch] Unhandled route:', url);
    return null;
  },
};

// ── Match data mapper (snake_case → camelCase for the UI) ──────────
function _mapMatch(m: any): any {
  if (!m) return m;
  const c1 = m.club1 || m.clubs || {};
  const c2 = m.club2 || {};
  return {
    ...m,
    id: m.id,
    club1Id: m.club1_id,
    club1Name: c1?.name || m.club1_name || '—',
    club1Logo: c1?.logo_url,
    club1Color: c1?.primary_color,
    club2Id: m.club2_id,
    club2Name: c2?.name || m.club2_name || '—',
    club2Logo: c2?.logo_url,
    club2Color: c2?.primary_color,
    tournamentName: m.tournaments?.name,
    venueName: m.venues?.name,
    format: m.format,
    status: m.status,
    scheduledAt: m.scheduled_at,
    innings1Runs: m.innings1_runs,
    innings1Wickets: m.innings1_wickets,
    innings1Overs: m.innings1_overs,
    innings2Runs: m.innings2_runs,
    innings2Wickets: m.innings2_wickets,
    innings2Overs: m.innings2_overs,
    resultText: m.result_text,
    winnerId: m.winner_id,
    momName: m.mom?.display_name || m.mom_name,
    momId: m.man_of_match_id,
    innings: m.innings,
  };
}

function _mapPlayer(p: any): any {
  if (!p) return p;
  const stats = Array.isArray(p.player_career_stats)
    ? p.player_career_stats[0]
    : p.player_career_stats;
  const clubs = Array.isArray(p.club_members)
    ? p.club_members.filter((m: any) => m.is_active).map((m: any) => ({
        id: m.clubs?.id, name: m.clubs?.name, logoUrl: m.clubs?.logo_url,
        primaryColor: m.clubs?.primary_color, clubRole: m.club_role, isActive: m.is_active,
      }))
    : [];
  return {
    id: p.id,
    userId: p.user_id,
    displayName: p.display_name,
    city: p.city,
    country: p.country,
    bio: p.bio,
    playerRole: p.player_role,
    battingStyle: p.batting_style,
    bowlingStyle: p.bowling_style,
    jerseyNumber: p.jersey_number,
    isFreeAgent: p.is_free_agent,
    profileVisibility: p.profile_visibility,
    mediaUrls: p.media_urls,
    batRuns: stats?.bat_runs,
    batMatches: stats?.bat_matches,
    batInnings: stats?.bat_innings,
    batBalls: stats?.bat_balls,
    batAverage: stats?.bat_innings && stats.bat_innings > stats.bat_not_outs
      ? ((stats.bat_runs || 0) / (stats.bat_innings - (stats.bat_not_outs || 0))).toFixed(1)
      : null,
    batSr: stats?.bat_balls && stats.bat_balls > 0
      ? (((stats.bat_runs || 0) / stats.bat_balls) * 100).toFixed(1)
      : null,
    batHighScore: stats?.bat_high_score,
    batFifties: stats?.bat_fifties,
    batHundreds: stats?.bat_hundreds,
    bowlWickets: stats?.bowl_wickets,
    bowlAverage: stats?.bowl_wickets && stats.bowl_wickets > 0
      ? ((stats.bowl_runs || 0) / stats.bowl_wickets).toFixed(1)
      : null,
    bowlEconomy: stats?.bowl_balls && stats.bowl_balls > 0
      ? (((stats.bowl_runs || 0) / stats.bowl_balls) * 6).toFixed(1)
      : null,
    catches: stats?.catches,
    manOfMatch: stats?.man_of_match,
    socialLinks: p.social_links || {},
    clubHistory: clubs,
  };
}

function _mapTournament(t: any): any {
  if (!t) return t;
  return {
    ...t,
    id: t.id,
    authorityId: t.authority_id,
    authorityName: t.authorities?.name || t.authority_name,
    authorityShort: t.authorities?.short_name,
    name: t.name,
    edition: t.edition,
    format: t.format,
    structure: t.structure,
    status: t.status,
    city: t.city,
    maxTeams: t.max_teams,
    entryFee: t.entry_fee,
    startDate: t.start_date,
    endDate: t.end_date,
    prizePool: t.prize_pool,
    approvedTeams: t.tournament_registrations?.[0]?.count ?? t.approved_teams,
    registrations: t.tournament_registrations,
    groups: t.tournament_groups,
    social_links: t.social_links,
  };
}

function _mapClub(c: any): any {
  if (!c) return c;
  const stats = Array.isArray(c.club_stats) ? c.club_stats[0] : c.club_stats;
  const members = (c.club_members || []).filter((m: any) => m.is_active && m.players?.profile_visibility !== 'private').map((m: any) => ({
    id: m.players?.id || m.id,
    displayName: m.players?.display_name || 'Unknown',
    playerRole: m.players?.player_role,
    city: m.players?.city,
    clubRole: m.club_role,
    adminRoles: Array.isArray(m.admin_roles) ? m.admin_roles : [],
    membershipStatus: m.membership_status,
    jerseyNumber: m.jersey_number,
    isActive: m.is_active,
    availabilityStatus: 'available',
    clubMemberId: m.id,
  }));
  return {
    id: c.id,
    name: c.name,
    shortName: c.short_name,
    city: c.city,
    country: c.country,
    logoUrl: c.logo_url,
    primaryColor: c.primary_color || '#1A8A4A',
    secondaryColor: c.secondary_color || '#0D5C2E',
    foundedYear: c.founded_year,
    homeGround: c.home_ground,
    bio: c.bio,
    social_links: c.social_links,
    wins: stats?.wins,
    losses: stats?.losses,
    draws: stats?.draws,
    totalMatches: stats?.total_matches,
    activeMembers: members.length,
    members,
  };
}

// ── Photo upload helper ──────────────────────────────────────
// MIME-allowlist + extension-allowlist to block uploads of HTML, SVG, PHP
// etc. that the storage CDN would serve with executable content-types.
const PHOTO_ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const PHOTO_ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);
const PHOTO_MAX_BYTES = 8 * 1024 * 1024; // 8 MB

export async function uploadPhoto(file: File, entityType: string, entityId: string): Promise<string> {
  if (!PHOTO_ALLOWED_MIME.has(file.type)) {
    throw new Error(`Unsupported file type "${file.type}". Allowed: JPEG, PNG, WebP, GIF.`);
  }
  if (file.size > PHOTO_MAX_BYTES) {
    throw new Error('File too large. Max 8 MB.');
  }
  const rawExt = (file.name.split('.').pop() || '').toLowerCase();
  const ext = PHOTO_ALLOWED_EXT.has(rawExt) ? rawExt : 'jpg';
  // entityType/entityId are caller-controlled — guard so they cannot embed
  // path traversal characters into the storage key.
  const safeType = String(entityType).replace(/[^a-z0-9_-]/gi, '').slice(0, 32);
  const safeId = String(entityId).replace(/[^a-z0-9_-]/gi, '').slice(0, 64);
  if (!safeType || !safeId) throw new Error('Invalid upload context.');
  const path = `${safeType}/${safeId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('photos').upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from('photos').getPublicUrl(path);
  return data.publicUrl;
}

// ── Delete key contact ───────────────────────────────────────
export async function deleteContact(contactId: string): Promise<void> {
  const { error } = await db.keyContacts().delete().eq('id', contactId);
  if (error) throw new Error(error.message);
}

export async function deletePhoto(photoId: string): Promise<void> {
  const { error } = await db.photoGallery().delete().eq('id', photoId);
  if (error) throw new Error(error.message);
}

// ══════════════════════════════════════════════════════════════════════════════
// useSocket — wraps Supabase Realtime to match the old Socket.io interface
// ══════════════════════════════════════════════════════════════════════════════

export function useSocket() {
  const channelsRef = useRef<RealtimeChannel[]>([]);
  const listenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());

  // Clean up channels on unmount. Use removeChannel (not unsubscribe) for
  // StrictMode safety — see realtime_notifications_quirk memory.
  useEffect(() => {
    return () => {
      channelsRef.current.forEach(ch => { try { supabase.removeChannel(ch); } catch { /* ignore */ } });
      channelsRef.current = [];
    };
  }, []);

  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(callback);
    return () => { listenersRef.current.get(event)?.delete(callback); };
  }, []);

  const emit = useCallback((event: string, data: any) => {
    listenersRef.current.get(event)?.forEach(cb => cb(data));
  }, []);

  const joinMatch = useCallback((matchId: string) => {
    // Subscribe to deliveries + innings updates for this match
    const ch = supabase
      .channel(`socket-match-${matchId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'deliveries',
      }, (payload) => {
        emit('score_update', {
          matchId,
          delivery: payload.new,
          innings: { totalRuns: payload.new.total_runs, totalWickets: payload.new.is_wicket ? 1 : 0 },
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'innings',
        filter: `match_id=eq.${matchId}`,
      }, (payload) => {
        emit('score_update', {
          matchId,
          innings: {
            totalRuns: payload.new.total_runs,
            totalWickets: payload.new.total_wickets,
            totalOvers: payload.new.total_overs,
          },
        });
      })
      .subscribe();
    channelsRef.current.push(ch);
  }, [emit]);

  const scoreBall = useCallback(async (data: {
    matchId: string; runsOffBat: number; isWicket: boolean; isLegalBall: boolean;
    [key: string]: any;
  }) => {
    // Direct insert to deliveries — Realtime will broadcast automatically
    const { error } = await db.deliveries().insert({
      innings_id: data.inningsId || data.matchId,
      over_number: data.overNumber || 0,
      ball_number: data.ballNumber || 0,
      bowler_id: data.bowlerId,
      striker_id: data.strikerId,
      runs_off_bat: data.runsOffBat,
      extra_runs: data.extraRuns || 0,
      total_runs: data.runsOffBat + (data.extraRuns || 0),
      is_legal_ball: data.isLegalBall,
      is_wicket: data.isWicket,
    });
    // Throw so callers (writeOrQueue in LiveScoring) can buffer to the
    // offline queue. A console.error here silently lost the ball.
    if (error) throw new Error(`scoreBall failed: ${error.message}`);
  }, []);

  return { on, joinMatch, scoreBall };
}
